import type { AcquisitionMetadata } from "utilities";

import { ApiError, apiRequest } from "./client";
import { API_BASE_URL } from "./config";
import type { AccountDto, AuthResponse, PublicProfileDto, UpdateProfilePayload } from "./types";

export type RegisterPayload = {
	email: string;
	fullName: string;
	password: string;
	/** Which link brought this person here. Omitted for direct arrivals and unavailable storage. */
	acquisition?: AcquisitionMetadata;
	/**
	 * The signup consent checkbox. The API rejects the registration unless this is true and records
	 * the acceptance against the new account, so it is not a formality the client may skip.
	 */
	acceptedTerms: boolean;
	/** Honeypot — the field is hidden on the register form, so only a bot ever fills it. The API
	 * answers those attempts exactly as it answers a taken email address, and creates nothing. */
	website?: string;
	/** Cloudflare Turnstile token. Absent when no site key is configured, which the API treats as
	 * "not checked" rather than as a failed challenge. */
	turnstileToken?: string;
};

export type LoginPayload = {
	email: string;
	password: string;
};

export const register = (payload: RegisterPayload): Promise<AuthResponse> =>
	apiRequest<AuthResponse>("/accounts/register", { method: "POST", body: payload });

export const login = (payload: LoginPayload): Promise<AuthResponse> =>
	apiRequest<AuthResponse>("/accounts/login", { method: "POST", body: payload });

/**
 * Signs in (provisioning the account on first use) with a Google ID token from Google Identity
 * Services. The attribution is only consumed when this call creates a new account; for a returning
 * user the API ignores it, so their original source is never overwritten.
 *
 * `acceptedTerms` follows the same rule: a returning user is signed in whatever it says, but the
 * API refuses to create an account without it. The sign-in page therefore sends false, and a new
 * user who starts there is sent to the register form to accept properly.
 */
export const googleLogin = (
	idToken: string,
	acquisition?: AcquisitionMetadata,
	acceptedTerms: boolean = false,
	turnstileToken: string = ""
): Promise<AuthResponse> =>
	apiRequest<AuthResponse>("/accounts/google", {
		method: "POST",
		// The token is only consulted on the branch that creates an account, so a returning user
		// signing in from a page with no challenge simply sends nothing.
		body: { idToken, acquisition, acceptedTerms, turnstileToken }
	});

export const getMe = (token: string): Promise<AccountDto> => apiRequest<AccountDto>("/accounts/me", { token });

/** Public profile of any user. Readable anonymously; the empty token simply omits the auth header. */
export const getPublicProfile = (userId: string, token: string): Promise<PublicProfileDto> =>
	apiRequest<PublicProfileDto>(`/accounts/${userId}/profile`, { token });

export const updateProfile = (payload: UpdateProfilePayload, token: string): Promise<AccountDto> =>
	apiRequest<AccountDto>("/accounts/profile", { method: "PUT", body: payload, token });

/** Uploads or replaces the profile picture (client-compressed beforehand; the API caps files at 2 MB). */
export const updateProfilePicture = (file: File, token: string): Promise<AccountDto> => {
	const formData = new FormData();
	formData.append("File", file);

	return apiRequest<AccountDto>("/accounts/profile/picture", { method: "POST", body: formData, token });
};

/** Removes the profile picture; the avatar falls back to initials. */
export const removeProfilePicture = (token: string): Promise<AccountDto> =>
	apiRequest<AccountDto>("/accounts/profile/picture", { method: "DELETE", token });

/** Starts an email change: the API emails a one-time passcode to the new address. */
export const requestEmailChange = (newEmail: string, token: string): Promise<void> =>
	apiRequest<void>("/accounts/change-email/request", { method: "POST", body: { newEmail }, token });

/** Confirms the email change with the OTP; the new address becomes the login email. */
export const confirmEmailChange = (otpCode: string, token: string): Promise<AccountDto> =>
	apiRequest<AccountDto>("/accounts/change-email/verify", { method: "POST", body: { otpCode }, token });

// The refresh token rides in an httpOnly cookie, so neither call sends or receives it in the body.
export const refreshToken = (): Promise<AuthResponse> =>
	apiRequest<AuthResponse>("/accounts/refresh-token", { method: "POST", body: {} });

export const logout = (): Promise<void> => apiRequest<void>("/accounts/logout", { method: "POST", body: {} });

/**
 * Deactivates the account: cancels any subscription's auto-renewal, hides all trees, and revokes every
 * session. Logging in again reactivates it; after 6 months the account is permanently deleted.
 */
export const deactivateAccount = (token: string): Promise<void> =>
	apiRequest<void>("/accounts/deactivate", { method: "POST", body: {}, token });

/**
 * Asks for the account to be deleted: it is deactivated now and erased after 30 days. Signing back in,
 * or a support ticket, before then restores it.
 */
export const requestAccountDeletion = (token: string): Promise<void> =>
	apiRequest<void>("/accounts/delete", { method: "POST", body: {}, token });

/**
 * Downloads the account and billing details as a JSON file ("Download my data"). Uses a raw fetch
 * because the endpoint returns the file itself rather than the JSON envelope apiRequest expects.
 */
export const downloadMyData = async (token: string): Promise<void> => {
	let response: Response;
	try {
		response = await fetch(`${API_BASE_URL}/api/v1/accounts/me/export`, {
			headers: { Authorization: `Bearer ${token}` },
			credentials: "include"
		});
	} catch {
		throw new ApiError("Unable to reach the server. Check your connection and try again.", 0);
	}

	if (!response.ok) {
		throw new ApiError("Your data could not be downloaded. Please try again.", response.status);
	}

	const disposition = response.headers.get("Content-Disposition") ?? "";
	const fileNameMatch = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(disposition);
	const fileName = fileNameMatch ? decodeURIComponent(fileNameMatch[1]) : "tree-tier-data.json";

	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};

export type ResetPasswordPayload = {
	token: string;
	newPassword: string;
};

export const forgotPassword = (email: string): Promise<void> =>
	apiRequest<void>("/accounts/forgot-password", { method: "POST", body: { email } });

export const resetPassword = (payload: ResetPasswordPayload): Promise<void> =>
	apiRequest<void>("/accounts/reset-password", { method: "POST", body: payload });

export const verifyEmail = (token: string): Promise<void> =>
	apiRequest<void>("/accounts/verify-email", { method: "POST", body: { token } });

export const resendVerification = (token: string): Promise<void> =>
	apiRequest<void>("/accounts/resend-verification", { method: "POST", body: {}, token });
